import { AfterViewInit, Component, DestroyRef, inject, Inject, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { showSpinner, TypedRoute } from '@shared/utils';
import { AuthService, SignupSessionPayload } from '@core/auth';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, switchMap, tap } from 'rxjs';
import { OnboardingService } from './onboarding.service';
import { ArivoStepperComponent } from '@shared/components/ui-elements';


@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrl: './onboarding.component.scss',
})
export class OnboardingComponent implements AfterViewInit, OnDestroy {
  destroyRef = inject(DestroyRef);
  @ViewChild('stepperComponent') stepperComponent?: ArivoStepperComponent;
  private shouldOpenThirdStep = false;

  constructor(
    @Inject(ActivatedRoute) private route: TypedRoute<{ token: string }>,
    private authService: AuthService,
    private onboardingService: OnboardingService,
    private router: Router,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    if (this.route.routeConfig?.path === 'onboarding/signup') {
      this.handleGoogleSignupCallback();
    } else {
      this.onboardingService.cleanSignupPayloadFromSession();
    }

    this.route.data.subscribe((data) => {
      if (data.token) {
        if (data.token === 'finish') {
          // If user is not authenticated, onboarding/finish assumes that finish is a token.
          // We need to inform user that he is not authenticated and forward to login (using logout flow)
          this.toastService.add({
            message: this.translateService.instant('Common.Errors.TokenInvalid'),
            type: 'error',
          });
          this.authService.logOut();
        } else {
          this.authService
            .logOutWithoutRedirect()
            .pipe(switchMap(() => this.authService.verifySignupJWT(data.token)))
            .pipe(showSpinner())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (data) => {
                if (!data.meta?.session_token) {
                  this.showUnknownErrorToast();
                  this.redirectToLogin();
                } else {
                  this.authService.logIn(data.meta.session_token, '/onboarding/finish');
                }
              },
              error: (error: HttpErrorResponse) => {
                this.authService.showTokenVerifyErrorToast(error);
                this.redirectToLogin();
              },
            });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.tryOpenThirdStep();
  }

  ngOnDestroy(): void {
    this.onboardingService.cleanSignupPayloadFromSession();
  }

  private handleGoogleSignupCallback(): void {
    const payload = this.onboardingService.loadSignupPayloadFromSession();

    if (!payload) {
      return;
    }

    this.authService
      .checkAuthStatus()
      .pipe(
        tap((resp) => {
          // If user is already authenticated, we can log them in and redirect to finish page.
          if (resp?.meta?.is_authenticated && resp?.meta?.session_token) {
            this.authService.logIn(resp.meta.session_token);
          } else {
            // If something went wrong (resp does not have session_token), fallback to signupScreen
            this.applySignupFallback(payload);
            this.showUnknownErrorToast();
          }
        }),
        catchError((error: HttpErrorResponse) => {
          // If user is not authenticated, we will proceed with google Signup
          if (!error.error?.meta?.is_authenticated && error.error?.data?.flows && this.authService.isGoogleSignupPending(error?.error)) {
            return this.performSSOProviderSignup(payload);
          }

          // Something went wrong with checkAuthStatus or tap, we will fallback to the signup Form and show "default" error
          this.applySignupFallback(payload);
          this.authService.showProviderSignupErrorToast();
          return EMPTY;
        }),
        showSpinner(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
  
  private performSSOProviderSignup(payload: SignupSessionPayload) {
    return this.authService.signUpWithSSOProvider(payload)
    .pipe(
      switchMap(() =>
        // After successful signup, we will check the auth status again to see if user is authenticated and has a session token.
        this.authService.checkAuthStatus().pipe(
          tap((resp) => {
            // If user is authenticated and has a session token, we will log them in and redirect to finish page.
            if (resp?.meta?.is_authenticated && resp?.meta?.session_token) {
              this.authService.logIn(resp.meta.session_token, '/onboarding/finish');
            } else {
              // If user is not authenticated after successful signup, we will redirect to login page and show "partial success" error
              this.redirectToLogin();
              this.authService.showProviderSignupPartialSuccess();
            }
          }),
          catchError((_followupStatusError: HttpErrorResponse) => {
            // If something went wrong after successful signup or tapping it, we will redirect to login page and show "partial success" error
            this.redirectToLogin();
            this.authService.showProviderSignupPartialSuccess();
            return EMPTY;
          }),
        ),
      ),
      catchError((signupError: HttpErrorResponse) => {
        // If signup fails, we will fallback to the signup Form and show error
        this.applySignupFallback(payload);

        if (this.authService.isTermsAgreedRequiredError(signupError)) {
          // If user tried to proceed without agreeing to terms, we will mark the checkbox as touched and show error message
          this.onboardingService.googleSignupTermsRequiredError.next(true);
          this.authService.showProviderSignupErrorToast('SignUp.TermsAgreedRequiredError');
        } else {
          this.onboardingService.googleSignupTermsRequiredError.next(false);
          this.authService.showProviderSignupErrorToast();
        }

        return EMPTY;
      }),
    );
  }


  private applySignupFallback(payload: SignupSessionPayload): void {
    if (payload.licensePlate) {
      this.onboardingService.licensePlate.next(payload.licensePlate);
    }

    this.shouldOpenThirdStep = true;
    this.tryOpenThirdStep();
  }

  private tryOpenThirdStep(): void {
    if (this.shouldOpenThirdStep && this.stepperComponent) {
      this.stepperComponent.selectedIndex = 2;
      this.shouldOpenThirdStep = false;
    }
  }

  showUnknownErrorToast() {
    this.toastService.add({
      message: this.translateService.instant('Common.Errors.UnknownError'),
      type: 'error',
    });
  }

  private redirectToLogin(): void {
    this.router.navigate(['/login']);
  }
}
